import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ElementBaseComponent } from '../ElementBaseComponent';
import { DataSet, ElementMetric, RowValue } from '../../../models/report-view.models';
import { HelpTextService } from '../../../services/services-index';

type GaugeState = 'success' | 'warning' | 'danger';
type TrendTone = 'positive' | 'negative' | 'neutral';

interface SupplementalMetricViewModel {
  label: string;
  value: string;
  trendValue: number | null;
  trendDisplay: string | null;
  supportingText?: string;
}

interface HoverInsightViewModel {
  label: string;
  leadValue: string;
  supportingText: string;
  tone: TrendTone;
}

interface GaugeThresholds {
  warningThreshold: number;
  successThreshold: number;
}

interface ConfiguredMetrics {
  actualMetric: ElementMetric;
  targetMetric: ElementMetric;
  supplementalMetrics: ElementMetric[];
  hoverMetrics: ElementMetric[];
}

interface MockKpiScenario {
  name: string;
  month: string;
  actual: { value: number; label: string };
  target: { value: number; label: string };
  prevPeriod: { value: string; trend: number; trendLabel: string };
  yoy: { value: string; trend: number; trendLabel: string };
  benchmark: { value: string; trend: number; trendLabel: string };
  hover: {
    progressValue: string;
    progressText: string;
    paceValue: string;
    paceText: string;
    todayValue: string;
    todayText: string;
  };
  flippedSummary: {
    mtdValue: string;
    paceValue: string;
  };
}

@Component({
  selector: 'kpi-pacing-gauge',
  templateUrl: './kpi-pacing-gauge.component.html',
  styleUrls: ['../panel-elements.scss', './kpi-pacing-gauge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class KpiPacingGaugeComponent extends ElementBaseComponent {
  // Supported settings.options keys:
  // kpiIdentifier, kpiIdentifierColumn, dateRangeColumn, warningThreshold, successThreshold,
  // progressPrecision, successColor, warningColor, dangerColor, trackColor,
  // supplementalKpiIdentifierColumn, supplementalDateRangeColumn,
  // (legacy fallback) supplementalLabelColumn, supplementalValueColumn, supplementalTrendColumn, supplementalMaxItems
  @Input() set dataSets(value: DataSet[]) {
    if (!!value && value.length > 0) {
      this.processData(value);
      this.isDataSetLoaded$.next(true);
    } else {
      // TODO: Remove this mock fallback once real datasets are wired for the KPI pacing gauge.
      this.processData(this.buildMockDataSets(), true);
      this.isDataSetLoaded$.next(true);
    }
  }

  readonly gaugeRadius = 86;
  readonly gaugeStrokeWidth = 22;
  readonly gaugeCircumference = 2 * Math.PI * this.gaugeRadius;

  isFlipped = false;

  cardTitle = '';
  metricLabel = '';
  actualDisplayValue = 'N/A';
  targetDisplayValue = 'N/A';
  progressDisplayValue = 'N/A';
  dateRangeDisplayValue = '';
  showHoverDetails = false;
  state: GaugeState = 'danger';
  stateColor = '#d64a4a';
  gaugeTrackColor = '#dddddd';
  gaugeDashOffset = this.gaugeCircumference;

  supplementalMetrics: SupplementalMetricViewModel[] = [];
  hoverInsights: HoverInsightViewModel[] = [];
  flippedSummaryMtdValue = 'N/A';
  flippedSummaryPaceValue = 'N/A';
  flippedSummaryPaceTone: TrendTone = 'neutral';
  private readonly actualScenarioNames = ['actual', 'primary', 'kpi', 'value'];
  private readonly targetScenarioNames = ['target', 'goal'];
  private readonly hoverScenarioNames = ['hover', 'mouseover', 'tooltip', 'narrative'];
  private readonly supplementalScenarioNames = ['supplemental', 'supporting', 'influence', 'influencer', 'driver', 'detail', 'back', 'flipped', 'associated', 'baseball', 'card'];
  private hoverMetricValues: SupplementalMetricViewModel[] = [];

  constructor(helpTextService: HelpTextService) {
    super(helpTextService);
  }

  toggleFlip(): void {
    this.isFlipped = !this.isFlipped;
    this.showHoverDetails = false;
  }

  onGaugeMouseEnter(): void {
    if (!this.isFlipped) {
      this.showHoverDetails = true;
    }
  }

  onGaugeMouseLeave(): void {
    this.showHoverDetails = false;
  }

  private processData(dataSets: DataSet[], usingMockData = false): void {
    const mainDataSet = dataSets[0];
    if (!mainDataSet || mainDataSet.rows.length === 0) {
      throw new Error('The KPI pacing gauge requires a populated primary dataset.');
    }

    const metricConfiguration = this.resolveConfiguredMetrics(usingMockData);
    const actualMetric = metricConfiguration.actualMetric;
    const targetMetric = metricConfiguration.targetMetric;
    const options = this.getOptions();
    const dimensionNames = this.getDimensionNames();

    const kpiIdentifierColumnName = this.resolveColumnName(
      mainDataSet,
      options['kpiIdentifierColumn'] ?? dimensionNames[0],
      'KpiName',
      usingMockData
    );
    const configuredKpiIdentifier = options['kpiIdentifier'] ?? options['kpiId'] ?? (usingMockData ? this.settings?.title : undefined);
    const dateRangeColumnName = this.resolveColumnName(
      mainDataSet,
      options['dateRangeColumn'] ?? dimensionNames[1],
      'Month',
      usingMockData
    );

    const kpiIdentifierColumnIndex = kpiIdentifierColumnName
      ? this.getColumnIndex(mainDataSet, kpiIdentifierColumnName, 'KPI identifier column')
      : -1;

    const selectedRow = this.getSelectedMainRow(mainDataSet, kpiIdentifierColumnIndex, configuredKpiIdentifier, usingMockData);
    const actualColumnIndex = this.getColumnIndex(mainDataSet, actualMetric.name, 'Actual metric column');
    const targetColumnIndex = this.getColumnIndex(mainDataSet, targetMetric.name, 'Target metric column');

    const actualNumericValue = this.toNumber(selectedRow[actualColumnIndex]?.value);
    const targetNumericValue = this.toNumber(selectedRow[targetColumnIndex]?.value);

    if (actualNumericValue === null) {
      throw new Error(`Actual metric value for '${actualMetric.name}' must be numeric.`);
    }

    if (targetNumericValue === null) {
      throw new Error(`Target metric value for '${targetMetric.name}' must be numeric.`);
    }

    this.cardTitle = this.getCardTitle(mainDataSet, selectedRow, kpiIdentifierColumnIndex, configuredKpiIdentifier);
    this.metricLabel = actualMetric.title ?? mainDataSet.columns[actualColumnIndex].displayName ?? actualMetric.name;
    this.actualDisplayValue = this.getDisplayValue(selectedRow[actualColumnIndex], actualNumericValue);
    this.targetDisplayValue = this.getDisplayValue(selectedRow[targetColumnIndex], targetNumericValue);
    this.dateRangeDisplayValue = this.getDateRangeDisplayValue(mainDataSet, selectedRow, dateRangeColumnName);

    const progressRatio = targetNumericValue > 0 ? actualNumericValue / targetNumericValue : 0;
    this.progressDisplayValue = targetNumericValue > 0 ? this.getProgressDisplay(progressRatio) : 'N/A';
    this.setState(progressRatio);
    this.gaugeDashOffset = this.gaugeCircumference * (1 - this.clamp(progressRatio, 0, 1));

    const detailDataSet = dataSets[1] ?? mainDataSet;
    this.supplementalMetrics = this.getSupplementalMetrics(
      detailDataSet,
      metricConfiguration.supplementalMetrics,
      options,
      configuredKpiIdentifier,
      kpiIdentifierColumnName,
      this.dateRangeDisplayValue,
      dateRangeColumnName
    );

    this.hoverMetricValues = this.getScenarioMetricValues(
      detailDataSet,
      metricConfiguration.hoverMetrics,
      configuredKpiIdentifier,
      kpiIdentifierColumnName,
      this.dateRangeDisplayValue,
      dateRangeColumnName,
      this.getOptionNumber('hoverMaxItems', 2)
    );

    this.setNarrativeDetails(usingMockData ? this.getMockScenario(this.cardTitle, this.dateRangeDisplayValue) : undefined);
  }

  getToneClass(tone: TrendTone): string {
    if (tone === 'positive') {
      return 'tone-positive';
    }

    if (tone === 'negative') {
      return 'tone-negative';
    }

    return 'tone-neutral';
  }

  getSupplementalMetricValueTone(metric: SupplementalMetricViewModel): TrendTone {
    return this.resolveTone(metric.trendValue, metric.value);
  }

  getSupplementalMetricTrendTone(metric: SupplementalMetricViewModel): TrendTone {
    return this.resolveTone(metric.trendValue, metric.trendDisplay ?? metric.value);
  }

  getSupplementalMetricTrendDisplay(metric: SupplementalMetricViewModel): string | null {
    if (metric.trendDisplay) {
      return metric.trendDisplay;
    }

    if (metric.trendValue === null) {
      return null;
    }

    const sign = metric.trendValue >= 0 ? '+' : '-';
    return `${sign}${Math.abs(metric.trendValue).toFixed(1)}% (MoM)`;
  }

  private resolveConfiguredMetrics(usingMockData = false): ConfiguredMetrics {
    const configuredMetrics = this.settings?.metrics ?? [];

    if (!usingMockData && configuredMetrics.length < 2) {
      throw new Error('KPI pacing gauge requires two metrics: [actual, target].');
    }

    const fallbackActualMetric: ElementMetric = { name: 'Actual', metricInverted: false, title: 'T3 Spend' };
    const fallbackTargetMetric: ElementMetric = { name: 'Target', metricInverted: false };

    const actualMetric = this.findMetricByScenario(configuredMetrics, this.actualScenarioNames)
      ?? configuredMetrics[0]
      ?? fallbackActualMetric;
    const targetMetric = this.findMetricByScenario(configuredMetrics, this.targetScenarioNames, [actualMetric.name])
      ?? configuredMetrics.find((metric) => metric.name !== actualMetric.name)
      ?? fallbackTargetMetric;

    if (!actualMetric?.name || !targetMetric?.name) {
      throw new Error('Both actual and target metrics must define a valid metric name.');
    }

    const configuredSupplementalMetrics = configuredMetrics.filter((metric) =>
      this.isSupplementalScenario(metric.scenario)
      && metric !== actualMetric
      && metric !== targetMetric
    );

    const hoverMetrics = configuredMetrics.filter((metric) =>
      this.isHoverScenario(metric.scenario)
      && metric !== actualMetric
      && metric !== targetMetric
    );

    const supplementalMetrics = usingMockData && configuredSupplementalMetrics.length === 0
      ? this.getMockSupplementalMetricDefinitions()
      : configuredSupplementalMetrics;

    return {
      actualMetric,
      targetMetric,
      supplementalMetrics,
      hoverMetrics
    };
  }

  private getSelectedMainRow(
    dataSet: DataSet,
    kpiIdentifierColumnIndex: number,
    configuredKpiIdentifier?: string,
    allowFallbackToFirstRow = false
  ): RowValue[] {
    if (!configuredKpiIdentifier) {
      return dataSet.rows[0];
    }

    if (kpiIdentifierColumnIndex < 0) {
      throw new Error('A kpiIdentifier was configured but no kpiIdentifierColumn was found.');
    }

    const normalizedIdentifier = configuredKpiIdentifier.trim().toLowerCase();
    const matchingRow = dataSet.rows.find((row) => {
      const rowIdentifier = row[kpiIdentifierColumnIndex]?.value?.toString().trim().toLowerCase();
      return rowIdentifier === normalizedIdentifier;
    });

    if (!matchingRow && allowFallbackToFirstRow) {
      return dataSet.rows[0];
    }

    if (!matchingRow) {
      throw new Error(`Unable to find KPI row for identifier '${configuredKpiIdentifier}'.`);
    }

    return matchingRow;
  }

  private getCardTitle(
    dataSet: DataSet,
    row: RowValue[],
    kpiIdentifierColumnIndex: number,
    configuredKpiIdentifier?: string
  ): string {
    if (this.settings?.title) {
      return this.settings.title;
    }

    if (configuredKpiIdentifier) {
      return configuredKpiIdentifier;
    }

    if (kpiIdentifierColumnIndex >= 0) {
      return row[kpiIdentifierColumnIndex]?.label ?? row[kpiIdentifierColumnIndex]?.value?.toString() ?? '';
    }

    return dataSet.columns.find((column) => column.type === 'metric')?.displayName ?? 'KPI Pacing';
  }

  private getDateRangeDisplayValue(dataSet: DataSet, row: RowValue[], dateRangeColumnName?: string): string {
    if (!dateRangeColumnName) {
      return '';
    }

    const dateRangeColumnIndex = this.getColumnIndex(dataSet, dateRangeColumnName, 'Date range column');
    const value = row[dateRangeColumnIndex];

    return value?.label ?? value?.value?.toString() ?? '';
  }

  private getProgressDisplay(progressRatio: number): string {
    const progressPrecision = this.getOptionNumber('progressPrecision', 0);
    return `${(progressRatio * 100).toFixed(progressPrecision)}%`;
  }

  private setState(progressRatio: number): void {
    const thresholds = this.getThresholds();

    if (progressRatio >= thresholds.successThreshold) {
      this.state = 'success';
    } else if (progressRatio >= thresholds.warningThreshold) {
      this.state = 'warning';
    } else {
      this.state = 'danger';
    }

    this.stateColor = this.getStateColor(this.state);
    this.gaugeTrackColor = this.getOption('trackColor') ?? '#dddddd';
  }

  private getThresholds(): GaugeThresholds {
    const warningThreshold = this.getOptionNumber('warningThreshold', 0.8);
    const successThreshold = this.getOptionNumber('successThreshold', 1);

    return {
      warningThreshold: Math.max(0, warningThreshold),
      successThreshold: Math.max(warningThreshold, successThreshold)
    };
  }

  private setNarrativeDetails(mockScenario?: MockKpiScenario): void {
    if (mockScenario) {
      this.flippedSummaryMtdValue = mockScenario.flippedSummary.mtdValue;
      this.flippedSummaryPaceValue = mockScenario.flippedSummary.paceValue;
      this.flippedSummaryPaceTone = this.resolveTone(null, this.flippedSummaryPaceValue);
      this.hoverInsights = [
        {
          label: 'Progress to Goal:',
          leadValue: mockScenario.hover.progressValue,
          supportingText: mockScenario.hover.progressText,
          tone: 'positive'
        },
        {
          label: 'Pace vs Benchmark:',
          leadValue: mockScenario.hover.paceValue,
          supportingText: mockScenario.hover.paceText,
          tone: this.resolveTone(null, mockScenario.hover.paceValue)
        },
        {
          label: 'Today\u2019s Contribution:',
          leadValue: mockScenario.hover.todayValue,
          supportingText: mockScenario.hover.todayText,
          tone: 'positive'
        }
      ];
      return;
    }

    const explicitHoverMetrics = this.hoverMetricValues;
    const paceMetric = this.getPreferredPaceMetric(explicitHoverMetrics.length > 0 ? explicitHoverMetrics : this.supplementalMetrics);
    const todayMetric = this.getPreferredTodayMetric(paceMetric);

    this.flippedSummaryMtdValue = this.progressDisplayValue;
    this.flippedSummaryPaceValue = this.getPaceDisplayValue(paceMetric);
    this.flippedSummaryPaceTone = this.resolveTone(paceMetric?.trendValue ?? null, this.flippedSummaryPaceValue);

    if (explicitHoverMetrics.length > 0) {
      const hoverInsights: HoverInsightViewModel[] = [];

      if (this.getOption('hoverIncludeProgress') !== 'false') {
        hoverInsights.push({
          label: 'Progress to Goal:',
          leadValue: this.progressDisplayValue,
          supportingText: this.getOption('hoverProgressText') ?? 'of target achieved',
          tone: this.resolveTone(this.state === 'danger' ? -1 : 1, this.progressDisplayValue)
        });
      }

      hoverInsights.push(...explicitHoverMetrics.map((metric) => ({
        label: metric.label.endsWith(':') ? metric.label : `${metric.label}:`,
        leadValue: metric.value,
        supportingText: metric.supportingText ?? '',
        tone: this.resolveTone(metric.trendValue, metric.value)
      })));

      this.hoverInsights = hoverInsights;
      return;
    }

    this.hoverInsights = [
      {
        label: 'Progress to Goal:',
        leadValue: this.progressDisplayValue,
        supportingText: this.getOption('hoverProgressText') ?? 'of target achieved',
        tone: this.resolveTone(this.state === 'danger' ? -1 : 1, this.progressDisplayValue)
      },
      {
        label: 'Pace vs Benchmark:',
        leadValue: this.flippedSummaryPaceValue,
        supportingText: this.flippedSummaryPaceTone === 'negative' ? 'behind ideal spend-to-date' : 'over ideal spend-to-date',
        tone: this.flippedSummaryPaceTone
      },
      {
        label: 'Today\u2019s Contribution:',
        leadValue: todayMetric?.value ?? this.actualDisplayValue,
        supportingText: 'spent today',
        tone: this.resolveTone(todayMetric?.trendValue ?? null, todayMetric?.value ?? this.actualDisplayValue)
      }
    ];
  }

  private getPreferredPaceMetric(metrics: SupplementalMetricViewModel[] = this.supplementalMetrics): SupplementalMetricViewModel | undefined {
    return metrics.find((metric) => /pace|benchmark|yoy/i.test(metric.label))
      ?? metrics.find((metric) => this.valueLooksLikePercentage(metric.value))
      ?? metrics[0];
  }

  private getPreferredTodayMetric(paceMetric?: SupplementalMetricViewModel): SupplementalMetricViewModel | undefined {
    return this.supplementalMetrics.find((metric) => metric !== paceMetric && !this.valueLooksLikePercentage(metric.value))
      ?? this.supplementalMetrics.find((metric) => metric !== paceMetric)
      ?? paceMetric;
  }

  private getPaceDisplayValue(metric?: SupplementalMetricViewModel): string {
    if (!metric) {
      return 'N/A';
    }

    if (this.valueLooksLikePercentage(metric.value)) {
      return metric.value;
    }

    if (metric.trendDisplay) {
      return metric.trendDisplay.replace(/\s*\(.*?\)\s*$/g, '');
    }

    if (metric.trendValue !== null) {
      const sign = metric.trendValue >= 0 ? '+' : '-';
      return `${sign}${Math.abs(metric.trendValue).toFixed(1)}%`;
    }

    return metric.value;
  }

  private valueLooksLikePercentage(value: string): boolean {
    return value.includes('%');
  }

  private resolveTone(trendValue: number | null, displayValue?: string): TrendTone {
    if (trendValue !== null && trendValue !== 0) {
      return trendValue > 0 ? 'positive' : 'negative';
    }

    const normalizedDisplayValue = displayValue?.trim();
    if (!normalizedDisplayValue) {
      return 'neutral';
    }

    if (normalizedDisplayValue.startsWith('+')) {
      return 'positive';
    }

    if (normalizedDisplayValue.startsWith('-')) {
      return 'negative';
    }

    return 'neutral';
  }

  private getMockScenario(cardTitle: string, month: string): MockKpiScenario | undefined {
    const normalizedCardTitle = cardTitle.trim().toLowerCase();
    const normalizedMonth = month.trim().toLowerCase();
    const scenarios = this.getMockScenarios();

    const exactMatch = scenarios.find((scenario) =>
      scenario.name.toLowerCase() === normalizedCardTitle
      && scenario.month.toLowerCase() === normalizedMonth
    );

    if (exactMatch) {
      return exactMatch;
    }

    return scenarios.find((scenario) => scenario.name.toLowerCase() === normalizedCardTitle);
  }

  private getSupplementalMetrics(
    supplementalDataSet: DataSet | undefined,
    supplementalMetricDefinitions: ElementMetric[],
    options: Record<string, string>,
    configuredKpiIdentifier: string | undefined,
    kpiIdentifierColumnName: string | undefined,
    selectedDateRange: string,
    dateRangeColumnName?: string
  ): SupplementalMetricViewModel[] {
    const maxSupplementalItems = this.getOptionNumber('supplementalMaxItems', 3);
    const scenarioMetricViewModels = this.getSupplementalMetricsByScenario(
      supplementalDataSet,
      supplementalMetricDefinitions,
      configuredKpiIdentifier,
      kpiIdentifierColumnName,
      selectedDateRange,
      dateRangeColumnName
    );

    if (scenarioMetricViewModels.length > 0) {
      return scenarioMetricViewModels.slice(0, Math.max(1, maxSupplementalItems));
    }

    return this.getSupplementalMetricsFromLegacyOptions(
      supplementalDataSet,
      options,
      configuredKpiIdentifier,
      kpiIdentifierColumnName,
      selectedDateRange,
      dateRangeColumnName
    );
  }

  private getScenarioMetricValues(
    supplementalDataSet: DataSet | undefined,
    supplementalMetricDefinitions: ElementMetric[],
    configuredKpiIdentifier: string | undefined,
    kpiIdentifierColumnName: string | undefined,
    selectedDateRange: string,
    dateRangeColumnName: string | undefined,
    maxItems: number
  ): SupplementalMetricViewModel[] {
    if (supplementalMetricDefinitions.length === 0) {
      return [];
    }

    return this.getSupplementalMetricsByScenario(
      supplementalDataSet,
      supplementalMetricDefinitions,
      configuredKpiIdentifier,
      kpiIdentifierColumnName,
      selectedDateRange,
      dateRangeColumnName
    ).slice(0, Math.max(1, maxItems));
  }

  private getSupplementalMetricsByScenario(
    supplementalDataSet: DataSet | undefined,
    supplementalMetricDefinitions: ElementMetric[],
    configuredKpiIdentifier: string | undefined,
    kpiIdentifierColumnName: string | undefined,
    selectedDateRange: string,
    dateRangeColumnName?: string
  ): SupplementalMetricViewModel[] {
    if (!supplementalDataSet || supplementalDataSet.rows.length === 0 || supplementalMetricDefinitions.length === 0) {
      return [];
    }

    const supplementalKpiIdentifierColumnIndex = kpiIdentifierColumnName
      ? supplementalDataSet.columns.findIndex((column) => column.name === kpiIdentifierColumnName)
      : -1;
    const supplementalDateRangeColumnIndex = dateRangeColumnName
      ? supplementalDataSet.columns.findIndex((column) => column.name === dateRangeColumnName)
      : -1;

    let filteredRows = supplementalDataSet.rows;
    if (configuredKpiIdentifier && supplementalKpiIdentifierColumnIndex >= 0) {
      const normalizedIdentifier = configuredKpiIdentifier.trim().toLowerCase();
      filteredRows = filteredRows.filter((row) => {
        const value = row[supplementalKpiIdentifierColumnIndex]?.value?.toString().trim().toLowerCase();
        return value === normalizedIdentifier;
      });
    }

    if (selectedDateRange && supplementalDateRangeColumnIndex >= 0) {
      const normalizedDateRange = selectedDateRange.trim().toLowerCase();
      filteredRows = filteredRows.filter((row) => {
        const label = row[supplementalDateRangeColumnIndex]?.label?.trim().toLowerCase();
        const value = row[supplementalDateRangeColumnIndex]?.value?.toString().trim().toLowerCase();
        return label === normalizedDateRange || value === normalizedDateRange;
      });
    }

    if (filteredRows.length === 0) {
      return [];
    }

    const selectedRow = filteredRows[0];
    return supplementalMetricDefinitions
      .map((metric): SupplementalMetricViewModel | null => {
        const valueColumnIndex = supplementalDataSet.columns.findIndex((column) => column.name === metric.name);
        if (valueColumnIndex < 0) {
          return null;
        }

        const valueItem = selectedRow[valueColumnIndex];
        const numericValue = this.toNumber(valueItem?.value);
        const value = this.getDisplayValue(valueItem, numericValue);

        let trendValue: number | null = null;
        let trendDisplay: string | null = null;
        if (metric.trend) {
          const trendColumnIndex = supplementalDataSet.columns.findIndex((column) => column.name === metric.trend);
          if (trendColumnIndex >= 0) {
            const trendItem = selectedRow[trendColumnIndex];
            trendValue = this.toNumber(trendItem?.value);
            trendDisplay = trendItem?.label ?? this.getTrendDisplayFromRawValue(trendItem?.value);
          }
        }

        return {
          label: metric.title ?? supplementalDataSet.columns[valueColumnIndex].displayName ?? metric.name,
          value,
          trendValue,
          trendDisplay,
          supportingText: metric.helpTextTitle
        };
      })
      .filter((metric): metric is SupplementalMetricViewModel => !!metric);
  }

  private getSupplementalMetricsFromLegacyOptions(
    supplementalDataSet: DataSet | undefined,
    options: Record<string, string>,
    configuredKpiIdentifier: string | undefined,
    kpiIdentifierColumnName: string | undefined,
    selectedDateRange: string,
    dateRangeColumnName?: string
  ): SupplementalMetricViewModel[] {
    if (!supplementalDataSet || supplementalDataSet.rows.length === 0) {
      return this.getFallbackSupplementalMetrics();
    }

    const supplementalKpiIdentifierColumnName = options['supplementalKpiIdentifierColumn'] ?? kpiIdentifierColumnName;
    const supplementalDateRangeColumnName = options['supplementalDateRangeColumn'] ?? dateRangeColumnName;

    const supplementalKpiIdentifierColumnIndex = supplementalKpiIdentifierColumnName
      ? supplementalDataSet.columns.findIndex((column) => column.name === supplementalKpiIdentifierColumnName)
      : -1;
    const supplementalDateRangeColumnIndex = supplementalDateRangeColumnName
      ? supplementalDataSet.columns.findIndex((column) => column.name === supplementalDateRangeColumnName)
      : -1;

    let supplementalRows = supplementalDataSet.rows;

    if (configuredKpiIdentifier && supplementalKpiIdentifierColumnIndex >= 0) {
      const normalizedIdentifier = configuredKpiIdentifier.trim().toLowerCase();
      supplementalRows = supplementalRows.filter((row) => {
        const value = row[supplementalKpiIdentifierColumnIndex]?.value?.toString().trim().toLowerCase();
        return value === normalizedIdentifier;
      });
    }

    if (selectedDateRange && supplementalDateRangeColumnIndex >= 0) {
      const normalizedDateRange = selectedDateRange.trim().toLowerCase();
      supplementalRows = supplementalRows.filter((row) => {
        const label = row[supplementalDateRangeColumnIndex]?.label?.trim().toLowerCase();
        const value = row[supplementalDateRangeColumnIndex]?.value?.toString().trim().toLowerCase();
        return label === normalizedDateRange || value === normalizedDateRange;
      });
    }

    if (supplementalRows.length === 0) {
      return this.getFallbackSupplementalMetrics();
    }

    const labelColumnName = options['supplementalLabelColumn'];
    const valueColumnName = options['supplementalValueColumn'];
    const trendColumnName = options['supplementalTrendColumn'];

    if (!labelColumnName && !valueColumnName) {
      return this.getFallbackSupplementalMetrics();
    }

    const labelColumnIndex = this.getSupplementalLabelColumnIndex(
      supplementalDataSet,
      labelColumnName,
      supplementalKpiIdentifierColumnName,
      supplementalDateRangeColumnName
    );
    const valueColumnIndex = this.getSupplementalValueColumnIndex(supplementalDataSet, valueColumnName);
    const trendColumnIndex = trendColumnName
      ? supplementalDataSet.columns.findIndex((column) => column.name === trendColumnName)
      : -1;

    const maxSupplementalItems = this.getOptionNumber('supplementalMaxItems', 3);
    const viewModels = supplementalRows
      .slice(0, Math.max(0, maxSupplementalItems))
      .map((row): SupplementalMetricViewModel => {
        const label = row[labelColumnIndex]?.label ?? row[labelColumnIndex]?.value?.toString() ?? '';
        const numericValue = this.toNumber(row[valueColumnIndex]?.value);
        const value = row[valueColumnIndex]?.label ?? (numericValue !== null ? this.formatNumber(numericValue) : row[valueColumnIndex]?.value?.toString() ?? '');
        const trendValue = trendColumnIndex >= 0 ? this.toNumber(row[trendColumnIndex]?.value) : null;
        const trendDisplay = trendColumnIndex >= 0
          ? row[trendColumnIndex]?.label ?? this.getTrendDisplayFromRawValue(row[trendColumnIndex]?.value)
          : null;

        return {
          label,
          value,
          trendValue,
          trendDisplay
        };
      })
      .filter((metric) => !!metric.label || !!metric.value);

    return viewModels.length > 0 ? viewModels : this.getFallbackSupplementalMetrics();
  }

  private getSupplementalLabelColumnIndex(
    dataSet: DataSet,
    labelColumnName: string | undefined,
    supplementalKpiIdentifierColumnName: string | undefined,
    supplementalDateRangeColumnName: string | undefined
  ): number {
    if (labelColumnName) {
      return this.getColumnIndex(dataSet, labelColumnName, 'Supplemental label column');
    }

    const excludedNames = new Set<string>([
      supplementalKpiIdentifierColumnName ?? '',
      supplementalDateRangeColumnName ?? ''
    ]);

    const preferredColumn = dataSet.columns.find((column) => column.type === 'dimension' && !excludedNames.has(column.name));
    if (preferredColumn) {
      return dataSet.columns.findIndex((column) => column.name === preferredColumn.name);
    }

    return 0;
  }

  private getSupplementalValueColumnIndex(dataSet: DataSet, valueColumnName: string | undefined): number {
    if (valueColumnName) {
      return this.getColumnIndex(dataSet, valueColumnName, 'Supplemental value column');
    }

    const firstMetricColumn = dataSet.columns.find((column) => column.type === 'metric');
    if (!firstMetricColumn) {
      throw new Error('Supplemental dataset requires a metric column or supplementalValueColumn option.');
    }

    return dataSet.columns.findIndex((column) => column.name === firstMetricColumn.name);
  }

  private getFallbackSupplementalMetrics(): SupplementalMetricViewModel[] {
    const fallback = [
      { label: 'Target', value: this.targetDisplayValue, trendValue: null, trendDisplay: null },
      { label: 'Progress', value: this.progressDisplayValue, trendValue: null, trendDisplay: null }
    ];

    if (this.dateRangeDisplayValue) {
      fallback.push({
        label: 'Period',
        value: this.dateRangeDisplayValue,
        trendValue: null,
        trendDisplay: null
      });
    }

    return fallback;
  }

  private getDisplayValue(value: RowValue, numericValue: number | null): string {
    if (value?.label) {
      return value.label;
    }

    if (numericValue === null) {
      return value?.value?.toString() ?? 'N/A';
    }

    return this.formatNumber(numericValue);
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1
    }).format(value);
  }

  private getTrendDisplayFromRawValue(value: unknown): string | null {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value) && value !== 0) {
      const sign = value > 0 ? '+' : '-';
      return `${sign}${Math.abs(value).toFixed(1)}% (MoM)`;
    }

    return null;
  }

  private getStateColor(state: GaugeState): string {
    if (state === 'success') {
      return this.getOption('successColor') ?? '#1bc23e';
    }

    if (state === 'warning') {
      return this.getOption('warningColor') ?? '#f29d38';
    }

    return this.getOption('dangerColor') ?? '#d64a4a';
  }

  private getColumnIndex(dataSet: DataSet, columnName: string, context: string): number {
    const columnIndex = dataSet.columns.findIndex((column) => column.name === columnName);
    if (columnIndex < 0) {
      throw new Error(`${context} '${columnName}' was not found in dataset '${dataSet.name}'.`);
    }

    return columnIndex;
  }

  private getOption(optionName: string): string | undefined {
    return this.getOptions()[optionName];
  }

  private findMetricByScenario(
    metrics: ElementMetric[],
    scenarioNames: string[],
    excludedMetricNames: string[] = []
  ): ElementMetric | undefined {
    const excludedNames = new Set(excludedMetricNames);
    return metrics.find((metric) => {
      const scenario = this.normalizeScenario(metric.scenario);
      return !excludedNames.has(metric.name) && scenarioNames.includes(scenario);
    });
  }

  private isSupplementalScenario(scenario?: string): boolean {
    return this.supplementalScenarioNames.includes(this.normalizeScenario(scenario));
  }

  private isHoverScenario(scenario?: string): boolean {
    return this.hoverScenarioNames.includes(this.normalizeScenario(scenario));
  }

  private normalizeScenario(scenario?: string): string {
    return scenario?.trim().toLowerCase() ?? '';
  }

  private resolveColumnName(
    dataSet: DataSet,
    preferredColumnName: string | undefined,
    fallbackColumnName: string,
    usingMockData: boolean
  ): string | undefined {
    if (preferredColumnName && dataSet.columns.some((column) => column.name === preferredColumnName)) {
      return preferredColumnName;
    }

    if (usingMockData && dataSet.columns.some((column) => column.name === fallbackColumnName)) {
      return fallbackColumnName;
    }

    return preferredColumnName;
  }

  private getOptionNumber(optionName: string, fallbackValue: number): number {
    const value = this.getOption(optionName);
    if (value === undefined || value === null || value === '') {
      return fallbackValue;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
  }

  private getOptions(): Record<string, string> {
    const rawOptions = this.settings?.options as unknown;

    if (!rawOptions) {
      return {};
    }

    if (Array.isArray(rawOptions)) {
      return rawOptions.reduce((optionsMap: Record<string, string>, entry: unknown) => {
        if (Array.isArray(entry) && entry.length > 1) {
          optionsMap[`${entry[0]}`] = `${entry[1]}`;
        }

        return optionsMap;
      }, {});
    }

    return rawOptions as Record<string, string>;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim();
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private buildMockDataSets(): DataSet[] {
    const metricConfiguration = this.resolveConfiguredMetrics(true);
    const actualMetricName = metricConfiguration.actualMetric.name;
    const targetMetricName = metricConfiguration.targetMetric.name;
    const supplementalMetrics = metricConfiguration.supplementalMetrics;
    const mockScenarios = this.getMockScenarios();

    const supplementalColumns = supplementalMetrics.flatMap((metric) => {
      const columns = [{
        name: metric.name,
        displayName: metric.title ?? metric.name,
        type: 'metric' as const,
        show: true
      }];

      if (metric.trend) {
        columns.push({
          name: metric.trend,
          displayName: `${metric.title ?? metric.name} Trend`,
          type: 'metric' as const,
          show: true
        });
      }

      return columns;
    });

    return [
      {
        name: 'kpi-pacing-gauge-mock-main',
        columns: [
          { name: 'KpiName', displayName: 'KPI', type: 'dimension', show: true },
          { name: 'Month', displayName: 'Month', type: 'dimension', show: true },
          { name: actualMetricName, displayName: this.settings?.metrics?.[0]?.title ?? 'T3 Spend', type: 'metric', show: true },
          { name: targetMetricName, displayName: 'Target', type: 'metric', show: true }
        ],
        rows: mockScenarios.map((scenario) => [
          { value: scenario.name },
          { value: scenario.month },
          { value: scenario.actual.value, label: scenario.actual.label },
          { value: scenario.target.value, label: scenario.target.label }
        ]),
        columnSets: []
      },
      {
        name: 'kpi-pacing-gauge-mock-supplemental',
        columns: [
          { name: 'KpiName', displayName: 'KPI', type: 'dimension', show: true },
          { name: 'Month', displayName: 'Month', type: 'dimension', show: true },
          ...supplementalColumns
        ],
        rows: mockScenarios.map((scenario) => [
          { value: scenario.name },
          { value: scenario.month },
          ...supplementalMetrics.flatMap((metric, index) => {
            const mockMetricValue = this.getMockSupplementalMetricValue(metric, index, scenario);
            const rowValues: RowValue[] = [{ value: mockMetricValue.value }];

            if (metric.trend) {
              rowValues.push({ value: mockMetricValue.trend, label: mockMetricValue.trendLabel });
            }

            return rowValues;
          })
        ]),
        columnSets: []
      }
    ];
  }

  private getMockSupplementalMetricValue(
    metric: ElementMetric,
    index: number,
    scenario: MockKpiScenario
  ): { value: string; trend: number; trendLabel: string } {
    const indicator = `${metric.scenario ?? ''} ${metric.title ?? ''} ${metric.name}`.toLowerCase();

    if (indicator.includes('prev')) {
      return scenario.prevPeriod;
    }

    if (indicator.includes('yoy')) {
      return scenario.yoy;
    }

    if (indicator.includes('bench')) {
      return scenario.benchmark;
    }

    if (index === 0) {
      return scenario.prevPeriod;
    }

    if (index === 1) {
      return scenario.yoy;
    }

    return scenario.benchmark;
  }

  private getMockSupplementalMetricDefinitions(): ElementMetric[] {
    return [
      {
        name: 'PrevPeriodMetric',
        metricInverted: false,
        trend: 'PrevPeriodTrend',
        title: 'Spend Pace to MTD Target',
        scenario: 'supplemental'
      },
      {
        name: 'YoyMetric',
        metricInverted: false,
        trend: 'YoyTrend',
        title: 'Impressions',
        scenario: 'supplemental'
      },
      {
        name: 'BenchmarkMetric',
        metricInverted: false,
        trend: 'BenchmarkTrend',
        title: 'Paid Search Impressions',
        scenario: 'supplemental'
      }
    ];
  }

  private getMockScenarios(): MockKpiScenario[] {
    return [
      {
        name: 'Demand Gen',
        month: 'January 2026',
        actual: { value: 124300, label: '$124.3k' },
        target: { value: 155400, label: '$155.4k' },
        prevPeriod: { value: '81%', trend: 2.2, trendLabel: '+2.2% (MoM)' },
        yoy: { value: '325.5M', trend: -1.8, trendLabel: '-1.8% (MoM)' },
        benchmark: { value: '10M', trend: 2, trendLabel: '+2.0% (MoM)' },
        hover: {
          progressValue: '81%',
          progressText: 'of target spend deployed',
          paceValue: '+6%',
          paceText: 'over ideal spend-to-date',
          todayValue: '$3.9k',
          todayText: 'spent today'
        },
        flippedSummary: {
          mtdValue: '81%',
          paceValue: '+6%'
        }
      },
      {
        name: 'Conversion',
        month: 'January 2026',
        actual: { value: 884000, label: '884k' },
        target: { value: 1700000, label: '1.7M' },
        prevPeriod: { value: '81%', trend: 2.2, trendLabel: '+2.2% (MoM)' },
        yoy: { value: '6M', trend: -8.8, trendLabel: '-8.8% (MoM)' },
        benchmark: { value: '38.3%', trend: -1.6, trendLabel: '-1.6% (MoM)' },
        hover: {
          progressValue: '51%',
          progressText: 'of target spend deployed',
          paceValue: '-33%',
          paceText: 'visitors short vs. expected',
          todayValue: '41k',
          todayText: 'visitors today'
        },
        flippedSummary: {
          mtdValue: '51%',
          paceValue: '-33%'
        }
      },
      {
        name: 'Opportunities',
        month: 'January 2026',
        actual: { value: 503900, label: '503.9k' },
        target: { value: 681000, label: '$681k' },
        prevPeriod: { value: '1.3%', trend: 1.2, trendLabel: '+1.2% (MoM)' },
        yoy: { value: '46.8k', trend: -1.8, trendLabel: '-1.8% (MoM)' },
        benchmark: { value: '5.4M', trend: 0.3, trendLabel: '+0.3% (MoM)' },
        hover: {
          progressValue: '74%',
          progressText: 'of lead target achieved',
          paceValue: '-1%',
          paceText: 'lead delta vs. expected',
          todayValue: '23.9k',
          todayText: 'leads today'
        },
        flippedSummary: {
          mtdValue: '74%',
          paceValue: '-1%'
        }
      },
      {
        name: 'Sales',
        month: 'January 2026',
        actual: { value: 59735, label: '59,735' },
        target: { value: 72100, label: '72.1k' },
        prevPeriod: { value: '7.9%', trend: 3.8, trendLabel: '+3.8% (MoM)' },
        yoy: { value: '2.3%', trend: 2.2, trendLabel: '+2.2% (MoM)' },
        benchmark: { value: '12min', trend: -1, trendLabel: '-1.0% (MoM)' },
        hover: {
          progressValue: '75%',
          progressText: 'of sales goal achieved',
          paceValue: '+1,950',
          paceText: 'sales over expected',
          todayValue: '2,844',
          todayText: 'sales today'
        },
        flippedSummary: {
          mtdValue: '81%',
          paceValue: '+73%'
        }
      }
    ];
  }
}
